import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IAppUser, USER_TYPE } from '~/types/app-user'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { keys as appsUsersKeys, useVerifyAppsUserMailMutation } from '~/queries/app-users'

import '../styles.scss'

interface Values {
  clientId: string
  email: string
}

const validationSchema = Yup.object().shape({
  clientId: Yup.string().required(FORM_ERRORS.REQUIRED),
  email: Yup.string().required(FORM_ERRORS.REQUIRED).email(FORM_ERRORS.INVALID_EMAIL),
})

interface ModalFormProps {
  email: string
  env: USER_TYPE
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  user: IAppUser
}

const VerifyMailForm = ({ email, env, onClose, onFailure, onSuccess, user }: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const defaultValues = {
    clientId: user.client_id,
    email: email,
  }

  /** Queries & Mutations */

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    setError,
  } = useForm<Values>({ defaultValues, resolver: yupResolver(validationSchema) })

  const { mutateAsync: verifyMail } = useVerifyAppsUserMailMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    const { clientId, email } = values

    if (email && clientId) {
      try {
        await verifyMail({ clientId, email, env, userId: user.user_id })
        queryClient.invalidateQueries({ queryKey: appsUsersKeys.details(env, user.user_id) })
        onSuccess('Email successfully verified!')
      } catch (e) {
        const apiError = e as AxiosError<IApiError>

        switch (apiError.response?.status) {
          case 409:
            setError('email', { message: 'This email is already verified' })
            break
          default:
            onFailure('Email could not be verified')
        }
      }
    }
  }

  return (
    <>
      <form className="verify-mail-form modal-form" id="verify-mail-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <TextInput
          error={errors.clientId?.message}
          label="Client id"
          placeholder="Enter a client id"
          type="text"
          {...register('clientId')}
        />
        <TextInput
          error={errors.email?.message}
          label="Email"
          placeholder={user.email_credentials ? user.email_credentials[0].email : 'Enter an email address'}
          {...register('email')}
        />
      </form>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!isValid} form="verify-mail-form" type="submit">
          Confirm
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </>
  )
}

interface ModalProps {
  email: string
  env: USER_TYPE
  isOpen: boolean
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  user?: IAppUser
}

const VerifyMailModal = ({ email, env, isOpen, onClose, onFailure, onSuccess, user }: ModalProps): ReactElement => {
  return (
    <Modal className="verify-mail-modal apps-users-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Edit user</ModalHeader>
      {user ? (
        <VerifyMailForm
          email={email}
          env={env}
          onClose={onClose}
          onFailure={onFailure}
          onSuccess={onSuccess}
          user={user}
        />
      ) : null}
    </Modal>
  )
}

export default VerifyMailModal
